import { c as _c } from "react/compiler-runtime";
import { StrikeWeightLevel } from '@/lib/piano/touch-design/hammer-weight-level';
import { StrikeWeightDesignMode, type StrikeWeightDesignTarget } from '@/lib/piano/touch-design/design/strike-weight-design';
import type { TouchWeightAnalyzedKeyboard } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import type { TouchDesignSerie } from '@/components/charts/interfaces';
import { useGenerateSerie } from '@/components/charts/hooks/use-generate-serie';
import { useCallback, useMemo } from 'react';
import { TouchDesignSerieVariant } from '@/components/charts/KeyboardChart';
import { SmoothStrategy } from '@/lib/geometry/curve-smoother/smooth-strategy.enum';
import { useSmoothedSerieLsr } from '@/components/charts/hooks/use-smoothed-serie-lsr';
import { useSmoothedSerieLoess } from '@/components/charts/hooks/use-smoothed-serie-loess';
import { useStrikeWeightCurve } from '@/hooks/standard-curves/use-strike-weight-curve';
import { useGenerateDesignedKeyboardFromSeries } from '@/hooks/keyboard/use-generate-designed-keyboard-from-series';
import { useInjection } from 'inversify-react';
import { strikeWeightCalculatorRequirementsSymbol, type StrikeWeightCalculatorRequirements } from '@/lib/piano/touch-design/strike-weight-calculator/strike-weight-calculator.requirements';
export const useStrikeWeightTargetSerie = keyboard => {
  const $ = _c(17);
  const {
    getStrikeWeightCurve
  } = useStrikeWeightCurve(keyboard);
  const {
    generateSerie
  } = useGenerateSerie(keyboard);
  let t0;
  if ($[0] !== getStrikeWeightCurve) {
    t0 = getStrikeWeightCurve(StrikeWeightLevel.Level5);
    $[0] = getStrikeWeightCurve;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      referenceCurve: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    smoothLsr
  } = useSmoothedSerieLsr(t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      bandwidthFraction: 1
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    smoothLoess
  } = useSmoothedSerieLoess(t2);
  const strikeWeightCalculator = useInjection(strikeWeightCalculatorRequirementsSymbol);
  const {
    generateDesignedKeyboardFromSeries
  } = useGenerateDesignedKeyboardFromSeries(keyboard);
  const name = "Target";
  const variant = TouchDesignSerieVariant.Target;
  let t3;
  if ($[5] !== generateSerie) {
    t3 = generateSerie(_temp, "Target", variant);
    $[5] = generateSerie;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const nullTargetSerie = t3;
  let t4;
  if ($[7] !== generateDesignedKeyboardFromSeries || $[8] !== generateSerie || $[9] !== getStrikeWeightCurve || $[10] !== nullTargetSerie || $[11] !== smoothLoess || $[12] !== smoothLsr || $[13] !== strikeWeightCalculator) {
    t4 = (mode, target, frontWeightTargetSerie, strikeWeightRatioTargetSerie, supportSpringBalanceWeightTargetSerie) => {
      if (mode === StrikeWeightDesignMode.AsMeasured) {
        return generateSerie(_temp2, "Target", variant);
      }
      if (target === null) {
        return nullTargetSerie;
      }
      if (mode === StrikeWeightDesignMode.StandardCurves && Object.values(StrikeWeightLevel).includes(target)) {
        const strikeWeightLevel = target as StrikeWeightLevel;
        const curve = getStrikeWeightCurve(strikeWeightLevel);
        return generateSerie(key_0 => curve?.[key_0.number - 1] ?? null, "Target", variant);
      }
      const baseSerie = generateSerie(_temp3, "Target", variant);
      if (target === SmoothStrategy.LeastSquaresRegression) {
        return smoothLsr(baseSerie);
      }
      if (target === SmoothStrategy.Loess) {
        return smoothLoess(baseSerie);
      }
      if (mode === StrikeWeightDesignMode.Computed && target !== null) {
        const keyboardWithTargetedStrikeWeight = generateDesignedKeyboardFromSeries({
          frontWeightTargetSerie,
          strikeWeightRatioTargetSerie,
          strikeWeightTargetSerie: nullTargetSerie,
          supportSpringBalanceWeightTargetSerie
        }).map(key_2 => strikeWeightCalculator.calculateStrikeWeight(key_2, Number(target)));
        return generateSerie(key_3 => keyboardWithTargetedStrikeWeight.getKeyByNumber(key_3.number)?.payload.strikeWeight ?? null, "Target", variant);
      }
      return nullTargetSerie;
    };
    $[7] = generateDesignedKeyboardFromSeries;
    $[8] = generateSerie;
    $[9] = getStrikeWeightCurve;
    $[10] = nullTargetSerie;
    $[11] = smoothLoess;
    $[12] = smoothLsr;
    $[13] = strikeWeightCalculator;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const generateStrikeWeightTargetSerie = t4;
  let t5;
  if ($[15] !== generateStrikeWeightTargetSerie) {
    t5 = {
      generateStrikeWeightTargetSerie
    };
    $[15] = generateStrikeWeightTargetSerie;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
function _temp() {
  return null;
}
function _temp2(key) {
  return key.payload.strikeWeight;
}
function _temp3(key_1) {
  return key_1.payload.strikeWeight;
}